import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useAuth0 } from "@auth0/auth0-react";
import { observer } from "mobx-react";
import { useStore, useAuth0TokenExchange } from "../../hooks";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Loading } from "../loading";
import ForbiddenPage from "../forbidenPage";
// Icon is now imported from @ann/web-components
import { useScrollToTop } from "../../contexts/ScrollToTopContext";
import { ShellLockProvider } from "../../contexts/ShellLockContext";
import { WebSocketProvider } from "../../contexts/WebSocketContext";
import { GlobalChatProvider } from "../../contexts/GlobalChatContext";
import { useWebSocketScope } from "../../hooks";
import { WebSocketScope } from "../../utils/enums";
import { Alert } from "@ann/web-components";
import {
  Wrapper,
  Content,
  Header,
  MenuWrapper,
  TitlePage,
  GlobalChatFloatingWrapper,
  SettingsUl,
  TopMenu,
  TopMenuAccountInfo,
  CustomAccountModalBodyListContainerIcon,
  TopNotification,
} from "./App.styled";
import { menuItems, settingsItems } from "./consts";
import ModalSubMenu from "./ModalSubMenu";
import CustomAccountModal from './CustomerAccountModal';
import { hideHelpContainer, adjustWidgetPosition, showHelpButton } from '../../elements/SupporButton/supportUtils';
import Popup from "../../elements/v2/Popup";
import Notifications from "../../elements/v2/Notifications";
import Spinner from "../common/Spinner";
import ChatSlideOut from "../../pages/v2/HomePageV2/components/ChatSlideOut";
import ChatErrorBoundary from "../../pages/v2/HomePageV2/components/ChatSlideOut/components/ChatErrorBoundary";
import {
  useHomeChatActions,
  useHomeChatState,
  useGlobalChatPanel,
  NATIVE_CHAT_ROUTES,
} from "../../contexts/GlobalChatContext";
import { useUserCheckPermissions } from "../../hooks/useUserCheckPermissions";
import SwitchWorkspaceModal from "../../elements/v2/SwitchWorkspaceModal";
import { Typography, Avatar, Sidebar, Icon } from "@ann/web-components";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { getAvatarUrl, generateAvatarColor } from "../../utils/avatarUtils";
import { runSignOut } from "../../utils/authLogout";

// ─── Global chat sub-components ───────────────────────────────────────────────
// Must be defined outside App so they can consume HomeChatContext, which is
// provided by GlobalChatProvider rendered inside App's return tree.

const GlobalChatTriggerButton = () => {
  const { openGlobalChat, closeGlobalChat } = useHomeChatActions();
  const { isGlobalChatOpen } = useHomeChatState();
  const location = useLocation();

  if (NATIVE_CHAT_ROUTES.some((r) => location.pathname.startsWith(r))) return null;

  return (
    <div
      onClick={() => isGlobalChatOpen ? closeGlobalChat() : openGlobalChat()}
      style={{ cursor: 'pointer' }}
    >
      <Icon color="neutral-white" name="aiChat" />
    </div>
  );
};

const GlobalChatPanel = () => {
  const { isGlobalChatOpen, isNativeChatPage, closeGlobalChat, handleOpenInHome } = useGlobalChatPanel();
  const isVisible = isGlobalChatOpen && !isNativeChatPage;

  return (
    <GlobalChatFloatingWrapper $isVisible={isVisible}>
      <ChatErrorBoundary>
        <ChatSlideOut onClose={closeGlobalChat} onOpenInHome={handleOpenInHome} isOpen={isVisible} />
      </ChatErrorBoundary>
    </GlobalChatFloatingWrapper>
  );
};

const theme = createTheme({
  palette: {
    primary: { main: "#3D3D3D" },
    secondary: { main: "#FF5722" },
  },
  typography: {
    fontFamily: 'Inter',
  },
});

const App = observer(() => {
	const { logout, isAuthenticated: isAuth0Authenticated } = useAuth0();
	useAuth0TokenExchange();
	const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
	const { authStore, title, notificationStore, profileStore, workspaceStore } = useStore();
	const navigate = useNavigate();
	const location = useLocation();
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
	const [isCustomAccountModalOpen, setIsCustomAccountModalOpen] = useState(false);
	const [activeMenuItem, setActiveMenuItem] = useState(null);
	const [customAccountModalItems, setCustomAccountModalItems] = useState([]);
	const [userModalOpen, setUserModalOpen] = useState(false);
	const [observerHelpButton, setObserver] = useState(null);
	const [fullName, setFullName] = useState("");
	const [accountImageFailed, setAccountImageFailed] = useState(false);
	const selectedAccountIdRef = useRef(null);
	const shellLockedRef = useRef(false);
	const { checkPermission } = useUserCheckPermissions();
	
	// State for TOAST Alert
	const [toastAlert, setToastAlert] = useState({
		show: false,
		title: "",
		description: "",
		severity: "info"
	});
	
	// ✅ Get scroll-to-top context
	const { containerRef, handleScroll } = useScrollToTop();

	// Detect if we're on workspace routes
	const isWorkspaceRoute = useMemo(
		() => location?.pathname?.startsWith("/v2/workspace"),
		[location?.pathname]
	);

	// Listen for TOAST events from NOTIFICATION scope
	useWebSocketScope(
		WebSocketScope.NOTIFICATION,
		(message) => {
			const payload = message.payload || {};
			const data = payload.data || payload;
			
			setToastAlert({
				show: true,
				title: payload.title || data.title || "Notification",
				description: payload.description || data.description || payload.message || data.message || "You have a new notification",
				severity: payload.severity || data.severity || "info"
			});
		},
		"TOAST"
	);

	const handleCloseToast = () => {
		setToastAlert(prev => ({ ...prev, show: false }));
	};

	useEffect(() => {
		if (!authStore.userData) return;

		const displayName = authStore.userData.fullName 
			|| `${authStore.userData.first_name || ''} ${authStore.userData.last_name || ''}`.trim();
		setFullName(displayName);

		let intervalId = null;
		let cancelled = false;

		const startPolling = async () => {
			if (!selectedAccountIdRef.current) return;
			const user_id = authStore.userData?.id;
			if (!user_id) return;
			const limit = 10;
			await getNotificationInternalRequest(user_id, limit);

			if (!cancelled) {
				intervalId = setInterval(() => {
					requestToGetNotifications(user_id, limit, null);
				}, 300000);
			}
		};

		startPolling();

		return () => {
			cancelled = true;
			if (intervalId) clearInterval(intervalId);
		};
	}, [authStore.userData])

	useEffect(() => {
		adjustWidgetPosition();
	}, []);

	// Load workspaces on init when we have the token (currentWorkspaceUuid)
	// This ensures the token's workspace is selected automatically
	useEffect(() => {
		if (authStore.currentWorkspaceUuid && 
			!workspaceStore.isLoading &&
			!authStore.isLoading) {
			
			// Check if currentWorkspace matches the token
			const currentWorkspaceUuid = workspaceStore.currentWorkspaceUuid;
			const tokenWorkspaceUuid = authStore.currentWorkspaceUuid;
			const needsSync = !currentWorkspaceUuid || 
			                 String(currentWorkspaceUuid) !== String(tokenWorkspaceUuid);
			
			// If the list is empty OR workspace doesn't match the token, reload
			if (workspaceStore.workspaceList.length === 0 || needsSync) {
				if (needsSync) {
					console.log('🔄 Workspace no coincide con token, recargando lista:', {
						current: currentWorkspaceUuid,
						token: tokenWorkspaceUuid
					});
				} else {
					console.log('🔄 Cargando lista de workspaces inicial desde token:', tokenWorkspaceUuid);
				}
				workspaceStore.resetPagination();
				workspaceStore.fetchPage(null, authStore);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authStore.currentWorkspaceUuid, authStore.isLoading]);

	useEffect(() => {
		// Only load tenants/workspace when in /v2/workspace route (if not loaded before)
		if (location?.pathname?.startsWith("/v2/workspace") && 
			workspaceStore.workspaceList.length === 0 && 
			!workspaceStore.isLoading) {
			workspaceStore.resetPagination();
			workspaceStore.fetchPage(null, authStore);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location?.pathname]);

	useEffect(() => {
		selectedAccountIdRef.current = authStore.selectedAccount?.id;
	}, [authStore.selectedAccount])

	const requestToGetNotifications = async (user_id, limit, id) => {
		await notificationStore.getNotificationsRequest({
			customer_id: selectedAccountIdRef.current, user_id, limit, id: null
		});
	}

	useEffect(() => {
		if (authStore.userId && authStore.customerId && !profileStore.displayPhotoURL) {
			profileStore.fetchUserData(authStore.userId, authStore.customerId);
		}
	}, [authStore.userId, authStore.customerId, profileStore.displayPhotoURL]);

	// ✅ Setup scroll-to-top functionality with Context
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		
		const onScroll = () => handleScroll(0.2);
		container.addEventListener('scroll', onScroll, { passive: true });
		
		// Initial check
		handleScroll(0.2);
		
		return () => container.removeEventListener('scroll', onScroll);
	}, [containerRef, handleScroll]);

	const getNotificationInternalRequest = async (user_id, limit) => {
		await authStore.fetchAllUsers()
		await requestToGetNotifications(user_id, limit, null);
	}

	useEffect(() => {
		const mutationObserver = new MutationObserver(() => {
		  hideHelpContainer();
		});
	
		setObserver(mutationObserver);
	
		return () => mutationObserver.disconnect();
	  }, []);

	  const observeIframe = useCallback(() => {
		const iframe = document.querySelector('iframe#jsd-widget');
		if (iframe) {
		  const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
		  if (observerHelpButton) {
			observerHelpButton.observe(iframeDocument, { childList: true, subtree: true });
		  }
		}
	  }, [observerHelpButton]);

	  useEffect(() => {
		const iframe = document.querySelector('iframe#jsd-widget');
		if (iframe) {
		  hideHelpContainer();
		  observeIframe();
		}
	  }, [observeIframe]);
	
	
	useEffect(() => {
		setIsSettingsOpen(false);
	}, [isMenuCollapsed]);
	
	const toggleMenu = (expanded) => {
		if (shellLockedRef.current) {
			setIsMenuCollapsed(true);
			return;
		}
		if (expanded !== undefined) {
			setIsMenuCollapsed(!expanded);
		} else {
			setIsMenuCollapsed(prev => !prev);
		}
	};
	
	const handleSignOut = useCallback(() => {
		setUserModalOpen(false);
		runSignOut(logout, { authStore, workspaceStore });
	}, [authStore, workspaceStore, logout]);
	
	const getAccountSelector = () => {
		if (!authStore.sortedAccountListByFavs || !Array.isArray(authStore.sortedAccountListByFavs)) {
			return [];
		}
		return authStore.sortedAccountListByFavs
			.filter((item) => item?.id)
			.map((item) => ({
				text: item.name,
				id: item.id,
				onClick: () => handleAccountSelector(item.id),
			}));
	}
	
	const handleAccountSelector = useCallback(
		(id) => {
			authStore.pickAccount(id);
		},
		[authStore]
	);

	const memoizedMenuItems = useMemo(() => {
		return menuItems(navigate).filter(item => authStore.hasPermission(item.permissionCode))
			.map(item => ({
				...item,
				customItems: item.customItems.filter(subitem => authStore.hasPermission(subitem.permissionCode)),
			}));
	}, [navigate, authStore.isSuperAdmin, authStore.isCustomerAdmin]);

	// Detect the active item based on the current route
	useEffect(() => {
		const currentPath = location.pathname;
		
		for (const item of memoizedMenuItems) {
			// Check if the item has a direct link and matches
			if (item.link) {
				const normalizedLink = item.link.replace(/\/$/, '');
				const normalizedPath = currentPath.replace(/\/$/, '');
				if (normalizedPath === normalizedLink || normalizedPath.startsWith(normalizedLink + '/')) {
					setActiveMenuItem(item.text);
					return;
				}
			}
			
			// Check if any subitem matches
			if (item.customItems && item.customItems.length > 0) {
				for (const subItem of item.customItems) {
					if (subItem.link) {
						const normalizedSubLink = subItem.link.replace(/\/$/, '');
						const normalizedPath = currentPath.replace(/\/$/, '');
						if (normalizedPath === normalizedSubLink || normalizedPath.startsWith(normalizedSubLink + '/')) {
							setActiveMenuItem(item.text);
							return;
						}
					}
				}
			}
		}
	}, [location.pathname, memoizedMenuItems]);

	const handleProfile = () => {
		navigate('/v2/user-management')
	}

	const memoizedSettingsItems = useMemo(() => {
		return settingsItems(navigate, handleSignOut, handleProfile);
	}, [navigate, handleSignOut]);

	
	const handleCustomAccountModal = (items) => {
		if(authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION")){
			setCustomAccountModalItems(items);
			setIsCustomAccountModalOpen(true);
		}
	}
	
	const closeAccountModal = async () => {
		setIsCustomAccountModalOpen(false);
		const user_id = authStore.userData?.id;
		if (!user_id) return;
		const limit = 10;
		await getNotificationInternalRequest(user_id, limit);
	};
	
	const handleUserModalOpen = (event) => {
		const rect = event.currentTarget.getBoundingClientRect();
		setModalPosition({ top: rect.bottom, left: rect.left });
		setUserModalOpen(true);
	};
	
	const handleUserModalClose = () => {
		setUserModalOpen(false);
	};
	
	const avatarCustomItems = useMemo(() => {
		const avatarItem = memoizedSettingsItems.find(item => item.text === "User");
		return avatarItem ? avatarItem.customItems : [];
	}, [memoizedSettingsItems]);

	const handleShellLockChange = useCallback((locked) => {
		shellLockedRef.current = locked;
		if (locked) {
			setIsMenuCollapsed(true);
		}
	}, []);
	
	if (authStore.isLoading) return <Loading />;

	// Sign-out in progress: Auth0 session still active but app user context was cleared.
	if (isAuth0Authenticated && !authStore.userData) {
		return <Loading />;
	}

	// TODO: Implement the ForbiddenPage logic
	// if (authStore.isError) return <ForbiddenPage />;
	// isError can be set by non-fatal calls (e.g. agent list 500). Only block the shell when
	// there is no admin bypass and no ACL from my-permissions yet.
	// This implementation is not working as expected, the ForbiddenPage is not being shown.
	const canUseAppDespiteLoadError =
		authStore.isGroupSuperAdmin ||
		authStore.isGroupAdmin ||
		authStore.isCustomerAdmin ||
		authStore.userPermissions.length > 0;
	if (authStore.isError && !canUseAppDespiteLoadError) return <ForbiddenPage />;

	return (
		<ShellLockProvider onLockChange={handleShellLockChange}>
		<WebSocketProvider>
		<GlobalChatProvider>
			<Wrapper isCollapsed={isMenuCollapsed} title={title} >
			<TopMenu>
				<TopMenuAccountInfo>
					{/* NEW: Avatar with avatarUtils logic - Always use workspace (unified) */}
					{(workspaceStore.currentWorkspace ) &&
					
					
						(() => {
							// Prefer workspace, fallback to selectedAccount if workspace not loaded yet
							const workspace = workspaceStore.currentWorkspace;
							const workspaceName = workspace?.tenant_name || 
							                      workspace?.name || 
							                      workspace?.workspaceName || 
							                      authStore.selectedAccount?.name || "";
							const photoUrl = workspace?.photo_url || 
							              workspace?.photoUrl ||
							              workspace?.profilePhotoUrl ||
							              authStore.selectedAccount?.photo_url ||
							              authStore.selectedAccount?.profilePhotoUrl;
							const avatarUrl = accountImageFailed
								? null
								: getAvatarUrl(photoUrl, workspaceName);
							const avatarColor = generateAvatarColor(workspaceName);
							const showInitialsAvatar = accountImageFailed || !avatarUrl;

							const handleAvatarClick = () => {
								if (authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION")) {
									handleCustomAccountModal(getAccountSelector());
								}
							};

							return (
								<div
									onClick={handleAvatarClick}
									style={{
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										backgroundColor: "transparent",
									}}
									data-testid="account-selector-icon"
								>
									{showInitialsAvatar ? (
										<NameInitialsAvatar
											name={workspaceName}
											bgColor={avatarColor}
											textColor="#ffffff"
											size="md"
											borderRadius="50%"
											textWeight="bold"
											textSize="14px"
											borderWidth="0"
										/>
									) : (
										<Avatar
											alt={workspaceName}
											size="md"
											src={avatarUrl}
											onError={() => setAccountImageFailed(true)}
										/>
									)}
								</div>
							);
						})()
					}
					
					{/* OLD: Legacy code using selectedAccount with route distinction - Commented out */}
					{/* 
					{isWorkspaceRoute ? (
						authStore.selectedAccount &&
						(() => {
							const accountName = authStore.selectedAccount.name || "";
							const accountId = authStore.selectedAccount.id || "";
							const photoUrl =
								authStore.selectedAccount.photo_url ||
								authStore.selectedAccount.profilePhotoUrl;
							const avatarUrl = accountImageFailed
								? null
								: getAvatarUrl(photoUrl, accountName);
							const avatarColor = generateAvatarColor(accountName);
							const showInitialsAvatar = accountImageFailed || !avatarUrl;

							const handleAvatarClick = () => {
								if (authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION")) {
									handleCustomAccountModal(getAccountSelector());
								}
							};

							return (
								<div
									onClick={handleAvatarClick}
									style={{
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										backgroundColor: "transparent",
									}}
									data-testid="account-selector-icon"
								>
									{showInitialsAvatar ? (
										<NameInitialsAvatar
											name={accountName}
											bgColor={avatarColor}
											textColor="#ffffff"
											size="md"
											borderRadius="50%"
											textWeight="bold"
											textSize="14px"
											borderWidth="0"
										/>
									) : (
										<Avatar
											alt={accountName}
											size="md"
											src={avatarUrl}
											onError={() => setAccountImageFailed(true)}
										/>
									)}
								</div>
							);
						})()
					) : (
						authStore.selectedAccount && (
						<CustomAccountModalBodyListContainerIcon
							data-testid="account-selector-icon"
							isSuperAdmin={authStore.isGroupSuperAdmin}
							onClick={() =>
								authStore.hasPermission("CUSTOM_ACCOUNT_MODAL_PERMISSION") &&
								handleCustomAccountModal(getAccountSelector())
							}
						>
							{`${authStore.selectedAccount.name
								.split(" ")
								.slice(0, 2)
								.map((word) => word[0])
								.join("")}`}
						</CustomAccountModalBodyListContainerIcon>
						)
					)}
					*/}
					
					{/* NEW: Nombre del workspace actual (unified, always use workspace) */}
					<Typography category="subtitle" size="small" weight="bold">
						{workspaceStore.currentWorkspace?.tenant_name ||
						 workspaceStore.currentWorkspace?.name ||
						 workspaceStore.currentWorkspace?.workspaceName ||
						 authStore.selectedAccount?.name ||
						 ""}
					</Typography>
					{/* {isWorkspaceRoute ? (
						// NEW: Current workspace name (fallback to account if workspace not loaded yet)
						<Typography category="subtitle" size="small" weight="bold">
							{workspaceStore.currentWorkspace?.name ||
								workspaceStore.currentWorkspace?.tenant_name ||
								authStore.selectedAccount?.name ||
								""}
						</Typography>
					) : (
						// OLD: Selected account name
						authStore.selectedAccount && (
						<Typography category="subtitle" size="small" weight="bold">
							{authStore.selectedAccount.name}
						</Typography>
						)
					)} */}
				</TopMenuAccountInfo>
				<TopNotification>
					<SettingsUl>
					{/* Chat - Primero */}
					{memoizedSettingsItems
						.filter(item => item.text === 'Chat')
						.map((item, index) => (
							(item.superAdminOnly && authStore.isGroupSuperAdmin) || !item.superAdminOnly ? (
								<GlobalChatTriggerButton key={`chat-${index}`} />
							) : null
						))}
						
						{/* Notificaciones - Segundo */}
						{authStore.hasPermission('NOTIFICATIONS_VIEW') && (
							<Popup
								header={<Notifications.Bell data-testid="notifications-icon" />}
								content={<Notifications />}
							/>
						)}
						
						{/* Support - Tercero */}
						{memoizedSettingsItems
							.filter(item => item.text === 'Support')
							.map((item, index) => (
								(item.superAdminOnly && authStore.isGroupSuperAdmin) || !item.superAdminOnly ? (
									<div 
										key={`support-${index}`}
										onClick={showHelpButton}
										style={{ cursor: 'pointer' }}
									>
										<Icon
											color="neutral-white"
											name="contact_support"
										/>
									</div>
								) : null
							))}
						
						{/* User - Fourth/Last */}
						{memoizedSettingsItems
							.filter(item => item.text === 'User')
							.map((item, index) => {
								const userFullName = fullName || authStore.userData?.fullName || `${authStore.userData?.first_name || ''} ${authStore.userData?.last_name || ''}`.trim();
								const avatarUrl = userFullName 
									? `https://ui-avatars.com/api/?name=${encodeURIComponent(userFullName)}`
									: null;
								
								return (item.superAdminOnly && authStore.isGroupSuperAdmin) || !item.superAdminOnly ? (
									<div 
										key={`user-${index}`}
										onClick={handleUserModalOpen}
										onMouseEnter={handleUserModalOpen}
										style={{ cursor: 'pointer' }}
									>
										{profileStore.isUpdatingProfile ? (
											<Spinner size={20}/>
										) : avatarUrl ? (
											<Avatar
												size="md"
												src={avatarUrl}
												alt={userFullName}
											/>
										) : (
											<Icon
												color="neutral-white"
												name="avatar"
											/>
										)}
									</div>
								) : null;
							})}

					</SettingsUl>
				</TopNotification>
			</TopMenu>

			<MenuWrapper isCollapsed={isMenuCollapsed} id="sidebar-menu-wrapper">
				<Sidebar
					expanded={!isMenuCollapsed}
					defaultExpanded={false}
					className="sidebar-menu" 
					position="relative"
					menuTopOffset={40}
					onToggle={toggleMenu}
				>
					{memoizedMenuItems.map((item, index) => {
						const itemKey = item.text || index;
						
						if (item.customItems && item.customItems.length > 0) {
							return (
								<Sidebar.Item
									key={itemKey}
									icon={item.icon}
									text={item.text}
									tooltip={isMenuCollapsed}
									isActive={activeMenuItem === item.text}
									data-testid={`menu-item-${item.text?.toLowerCase()}`}
								>
									<Sidebar.Menu>
										{item.customItems.map((subItem, subIndex) => (
												<Sidebar.Menu.Item
													key={subItem.text || subIndex}
													title={subItem.text}
													icon={subItem.icon}
													onClick={() => {
													setActiveMenuItem(item.text);
														if (subItem.onClick) {
															subItem.onClick();
														}
													}}
												/>
										))}
									</Sidebar.Menu>
								</Sidebar.Item>
							);
						} else {
							return (
								<Sidebar.Item
									key={itemKey}
									icon={item.icon}
									text={item.text}
									tooltip={isMenuCollapsed}
									isActive={activeMenuItem === item.text}
									onClick={() => {
										setActiveMenuItem(item.text);
										if (item.link) {
											navigate(item.link);
										}
									}}
									data-testid={`menu-item-${item.text?.toLowerCase()}`}
								/>
							);
						}
					})}
				</Sidebar>
			</MenuWrapper>

        {/* {title && (
				<Header showDivision={title}>
					<TitlePage>{title}</TitlePage>
				</Header>
			)} */}

			<Content id="main-content" ref={containerRef}>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<Outlet />
				</ThemeProvider>
			</Content>

		<GlobalChatPanel />


			{/* SWITCH WORKSPACE MODAL - Show in all routes (global, preferred) */}
			{isCustomAccountModalOpen && (
				<SwitchWorkspaceModal
					isOpen={isCustomAccountModalOpen}
					onClose={closeAccountModal}
					onWorkspaceSelect={(workspace) => {
						console.log("Workspace seleccionado:", workspace);
						closeAccountModal();
					}}
				/>
			)}

			{/* CUSTOM ACCOUNT MODAL - Legacy fallback (only if SwitchWorkspaceModal is not available) */}
			{/* Note: This is kept for backward compatibility but SwitchWorkspaceModal is preferred */}

			{userModalOpen && authStore.userData && (
				<ModalSubMenu
					position={modalPosition}
					title={fullName}
					subtitle={authStore.userData.email ?? ""}
					items={avatarCustomItems}
					onClose={handleUserModalClose}
					avatar={true}
				/>
			)}

			{toastAlert.show && (
				<Alert
					title={toastAlert.title}
					description={toastAlert.description}
					severity={toastAlert.severity}
					maxWidth="500px"
					closable={true}
					autoClose={true}
					autoCloseDelay={5000}
					onClose={handleCloseToast}
				/>
			)}
		</Wrapper>
		</GlobalChatProvider>
		</WebSocketProvider>
		</ShellLockProvider>
	);
});

export default App;

